import React, { useMemo } from 'react';
import { Check, ChevronRight, Plus, UserRound, X } from 'lucide-react';
import { AREA_HINT, AREA_LABEL } from './areas';
import { C, MobileSheet} from './MobileShell';
import { listSavedAppAccounts, switchableAreas } from './savedAppAccounts';

/**
 * Sheet: Bereich wechseln · andere Konten · Neue Registrierung.
 */
export default function ProfileAccountSheet({
  me,
  area,
  onClose,
  onPickArea,
  onSwitchAccount,
  onNewRegistration,
  onLoginOther,
}) {
  const areas = useMemo(() => switchableAreas(me?.areas || []), [me?.areas]);
  const others = useMemo(
    () => listSavedAppAccounts().filter((a) => a.id !== String(me?.id || '')),
    [me?.id],
  );

  return (
    <MobileSheet
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
      data-testid="mobile-profile-sheet"
    >
      <div
        className="w-full max-w-md rounded-t-2xl p-4 space-y-3 max-h-[85vh] overflow-y-auto"
        style={{ background: C.card }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1 px-1">
          <div className="min-w-0">
            <p className="text-sm font-bold truncate" data-testid="mobile-profile-sheet-name">
              {me?.full_name || me?.email || 'Konto'}
            </p>
            <p className="text-xs" style={{ color: C.dim }}>
              Bereich und Nutzer wechseln
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="p-1.5 rounded-full"
            style={{ color: C.dim }}
            data-testid="mobile-profile-sheet-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {areas.length > 0 && (
          <section data-testid="mobile-profile-areas">
            <p className="text-[11px] font-semibold uppercase tracking-wide px-1 mb-1.5" style={{ color: C.dim }}>
              Bereich
            </p>
            <div className="space-y-2">
              {areas.map((a) => {
                const active = a === area || (a === 'lehrer' && area === 'personal')
                  || (a === 'schueler' && area === 'privat');
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => onPickArea(a)}
                    data-testid={`mobile-profile-area-${a}`}
                    className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left active:opacity-80"
                    style={{
                      background: active ? C.rose : C.bg,
                      border: `1px solid ${active ? C.red : C.border}`,
                    }}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold">
                        {a === 'verwaltung' ? 's-admin (Verwaltung)' : (AREA_LABEL[a] || a)}
                      </span>
                      <span className="block text-xs mt-0.5" style={{ color: C.dim }}>
                        {AREA_HINT[a] || (active ? 'Aktuell aktiv' : 'Wechseln')}
                      </span>
                    </span>
                    {active
                      ? <Check className="w-4 h-4 shrink-0" style={{ color: C.red }} />
                      : <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.dim }} />}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {others.length > 0 && (
          <section data-testid="mobile-profile-accounts">
            <p className="text-[11px] font-semibold uppercase tracking-wide px-1 mb-1.5" style={{ color: C.dim }}>
              Andere Konten
            </p>
            <div className="space-y-2">
              {others.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onSwitchAccount(a)}
                  data-testid={`mobile-profile-switch-${a.id}`}
                  className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left active:opacity-80"
                  style={{ background: C.bg, border: `1px solid ${C.border}` }}
                >
                  <UserRound className="w-5 h-5 shrink-0" style={{ color: C.red }} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold truncate">{a.full_name || a.email || 'Konto'}</span>
                    {a.email && (
                      <span className="block text-xs truncate mt-0.5" style={{ color: C.dim }}>{a.email}</span>
                    )}
                  </span>
                  <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.dim }} />
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="space-y-2 pt-1">
          <button
            type="button"
            onClick={onNewRegistration}
            data-testid="mobile-profile-new-registration"
            className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left active:opacity-80"
            style={{ background: C.rose, border: `1px solid ${C.border}` }}
          >
            <Plus className="w-5 h-5 shrink-0" style={{ color: C.red }} />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold">Neue Registrierung</span>
              <span className="block text-xs mt-0.5" style={{ color: C.dim }}>
                Weiteres Konto anlegen (Lehrer, Schüler oder Eltern)
              </span>
            </span>
            <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.dim }} />
          </button>
          <button
            type="button"
            onClick={onLoginOther}
            data-testid="mobile-profile-login-other"
            className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left active:opacity-80"
            style={{ background: C.bg, border: `1px solid ${C.border}` }}
          >
            <UserRound className="w-5 h-5 shrink-0" style={{ color: C.red }} />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold">Anderes Konto anmelden</span>
              <span className="block text-xs mt-0.5" style={{ color: C.dim }}>
                Mit E-Mail und Passwort wechseln
              </span>
            </span>
            <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.dim }} />
          </button>
        </section>
      </div>
    </MobileSheet>
  );
}
